import React from 'react';
import { BookOpen, FileText, Send } from 'lucide-react';
import { publications } from '../data/mock';
import publicationHero from "../assets/images/publicationBG.webp";

const Publications = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="gradient-hero-saffron py-20"
        style={{
          backgroundImage: `
    linear-gradient(
      to bottom,
      rgba(255,247,230,0.85),
      rgba(192,119,51,0.8)
    ),
    url('${publicationHero}')
  `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}

      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground font-serif">
              Publications
            </h1>
            <p className="text-xl text-muted-foreground">
              Scholarly contributions to Indian Knowledge Systems research
            </p>
          </div>
        </div>
      </section>

      {/* Research Journal */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <BookOpen className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-4 font-serif">
                  {publications.journal.name}
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p className="flex items-center gap-2">
                    <span className="font-semibold text-foreground">Frequency:</span>
                    {publications.journal.frequency}
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-semibold text-foreground">ISSN:</span>
                    {publications.journal.issn}
                  </p>
                  <p className="leading-relaxed">
                    {publications.journal.description}
                  </p>
                </div>
                <button className="mt-6 btn-saffron">
                  Access Journal
                </button>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-primary/10 to-orange-400/10 rounded-2xl p-8 shadow-xl border-2 border-primary/20">
                  <div className="bg-white rounded-lg p-6 shadow-lg">
                    <div className="h-64 bg-gradient-to-br from-primary/20 to-orange-400/20 rounded-lg flex items-center justify-center mb-4">
                      <BookOpen className="w-24 h-24 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground text-center">
                      Latest Issue
                    </h3>
                    <p className="text-center text-muted-foreground text-sm mt-2">
                      Volume 1, Issue 1
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call for Papers */}
      {publications.callForPapers.open && (
        <section className="py-16 bg-secondary/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Send className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-foreground font-serif">
                      Call for Papers
                    </h2>
                    <p className="text-primary font-semibold">
                      Now Accepting Submissions
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      We invite original research papers, review articles, and case studies on topics related to Indian Knowledge Systems.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-foreground mb-3">Submission Deadline:</h3>
                    <p className="text-primary font-semibold text-lg">
                      {publications.callForPapers.deadline}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-bold text-foreground mb-3">Topics of Interest:</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {publications.callForPapers.topics.map((topic, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-gradient-to-br from-secondary/20 to-white p-3 rounded-lg border border-primary/10"
                        >
                          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-foreground">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 mt-4">

                    <a
                      href={publications.callForPapers.link}
                      target="_blank"
                      className="btn-saffron"
                    >
                      Submit Paper
                    </a>

                    <a
                      href={publications.callForPapers.guidelines}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline-saffron"
                    >
                      Guidelines
                    </a>

                    <a
                      href={publications.callForPapers.brochure}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline-saffron"
                    >
                      Brochure
                    </a>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Publications */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-8 text-center font-serif">
              Other Publications
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Books & Edited Volumes',
                  description: 'Comprehensive scholarly works on various aspects of IKS',
                  icon: BookOpen
                },
                {
                  title: 'Research Articles',
                  description: 'Peer-reviewed articles in national and international journals',
                  icon: FileText
                },
                {
                  title: 'Reports & Monographs',
                  description: 'Detailed research reports and analytical studies',
                  icon: FileText
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-secondary/20 p-6 rounded-xl shadow-lg hover-lift border border-primary/10"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary to-orange-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold font-serif">
              Contribute to Our Research
            </h2>
            <p className="text-xl text-white/90">
              Join us in advancing scholarship in Indian Knowledge Systems
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Publications;
